import { use$ } from '@legendapp/state/react';
import { useCallback, useEffect } from 'react';
import useGameSound from 'use-sound';
import { uiState$ } from '../state/ui.state';

export type SoundType = 'win' | 'loss' | 'click' | 'cashout';

/**
 * A hook for playing sound effects in the application
 * @param type The type of sound to play ('roll', 'win', or 'slide')
 * @param options Optional configuration options for the sound
 * @returns An object with functions to control the sound
 */
export const useSound = (
  type: SoundType,
  options?: {
    volume?: number;
    interrupt?: boolean;
    playbackRate?: number;
    keepSoundAlive?: boolean;
  }
) => {
  const soundTurnedOn = use$(uiState$.soundTurnedOn);

  // Use the use-sound hook with our sound file
  const [play, { stop, pause, duration, sound }] = useGameSound(
    `/sound/${type}.mp3`,
    {
      volume: options?.volume ?? 0.15,
      interrupt: options?.interrupt ?? false,
      soundEnabled: soundTurnedOn, // Connect to our global sound setting
      playbackRate: options?.playbackRate ?? 1,
      id: type,
    }
  );

  // Create a wrapper function that respects our global sound setting
  const playSound = useCallback(() => {
    if (soundTurnedOn) {
      play();
    }
  }, [play, soundTurnedOn]);

  // Clean up sound resources when the component unmounts
  useEffect(() => {
    return () => {
      if (options?.keepSoundAlive) {
        return;
      }
      // Clean up any playing sounds when component unmounts
      if (sound?.playing()) {
        stop();
      }
    };
  }, [sound, stop, options?.keepSoundAlive]);

  return {
    playSound,
    stop,
    pause,
    duration,
    sound,
    isEnabled: soundTurnedOn,
  };
};
